import * as notificationService from '../../services/notification.service';

import {
  AcknowledgeMessageParams,
  FetchMessagesParams,
  MessageResponse,
  NotificationMessage,
  NotificationState
} from '../../models/notification-model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { APIAsyncHeaders } from '../../models/login-model';
import { openAlertAsync } from './alertSlice';

// Transform API response to NotificationMessage format
const transformMessage = (message: MessageResponse): NotificationMessage => {
  return {
    messageId: message.messageId,
    subject: message.subject,
    body: message.body,
    storeId: message.storeId,
    createdAt: message.createdAt,
    purposeName: message.purposeName,
    isRead: message.vendorAckAt !== null // Consider read if vendor acknowledged
  };
};

// Async thunk to fetch messages
export const fetchMessagesAsync = createAsyncThunk<
  MessageResponse[],
  FetchMessagesParams & { headers?: APIAsyncHeaders },
  { rejectValue: string }
>('notifications/fetchMessages', async ({ vendorUserId, deviceToken }, { dispatch, rejectWithValue }) => {
  try {
    const response = await notificationService.fetchMessages(vendorUserId, deviceToken);
    return response;
  } catch (error: any) {
    const errorMsg = error.message || error.errorMessage || 'Failed to fetch messages.';
    dispatch(openAlertAsync(errorMsg));
    return rejectWithValue(errorMsg);
  }
});

// Async thunk to acknowledge a message
export const acknowledgeMessageAsync = createAsyncThunk<
  { messageId: number },
  AcknowledgeMessageParams & { headers?: APIAsyncHeaders },
  { rejectValue: string }
>(
  'notifications/acknowledgeMessage',
  async ({ messageId, vendorAckBy, deviceToken }, { dispatch, rejectWithValue }) => {
    try {
      await notificationService.acknowledgeMessage({ messageId, vendorAckBy, deviceToken });
      return { messageId };
    } catch (error: any) {
      const errorMsg = error.message || error.errorMessage || 'Failed to acknowledge message.';
      dispatch(openAlertAsync(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

// Initial state
const initialState: NotificationState = {
  messages: [],
  unreadCount: 0,
  isLoading: false,
  error: null
};

// Notification slice
const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    acknowledgeMessage: (state, action) => {
      const messageId = action.payload;
      const message = state.messages.find((msg) => msg.messageId === messageId);
      if (message && !message.isRead) {
        message.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    acknowledgeAllMessages: (state) => {
      state.messages.forEach((message) => {
        message.isRead = true;
      });
      state.unreadCount = 0;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessagesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessagesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.messages = action.payload.map(transformMessage);
        state.unreadCount = state.messages.filter((msg) => !msg.isRead).length;
        state.error = null;
      })
      .addCase(fetchMessagesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch messages';
      })
      .addCase(acknowledgeMessageAsync.pending, (state) => {
        // Optionally handle loading state for acknowledge
      })
      .addCase(acknowledgeMessageAsync.fulfilled, (state, action) => {
        const messageId = action.payload.messageId;
        const message = state.messages.find((msg) => msg.messageId === messageId);
        if (message && !message.isRead) {
          message.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(acknowledgeMessageAsync.rejected, (state, action) => {
        state.error = action.payload || 'Failed to acknowledge message';
      });
  }
});

export const { acknowledgeMessage, acknowledgeAllMessages, clearError } = notificationSlice.actions;
export const notificationReducer = notificationSlice.reducer;
export type { NotificationState };
